import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { TwMainLayout } from "../../components/tailwind";
import { classNames } from "../../helpers";
import { useToast } from "../../provider/ToastProvider";
import * as dashboardActions from "../../redux/reducers/dashboardReducer";
import { dashboardState } from "../../redux/reducers/dashboardReducer";
import { setGlobalLoading, setSectionLoading } from "../../redux/reducers/loadingReducer";
import { userState } from "../../redux/reducers/userReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses } from "../../utils/confighelpers";
import AddParticipantDrawer from "./schedule/AddParticipantDrawer";
import AddSessionsDrawer from "./schedule/AddSessionsDrawer";
import { SessionCoachDrawer, SessionLocationDrawer, SessionTimeDrawer } from "./schedule/SessionEditDrawers";
import { FcConfirmDialog } from "./schedule/ui";
import "../../styles/onboarding.css";

/**
 * My Schedule (new design): week time-grid + list view of sessions.
 * Clicking a session opens a side drawer with its participants and an actions menu. Each action opens a
 * centred pop-up ON TOP of that drawer (schedule/AddParticipantDrawer, schedule/SessionEditDrawers, or a
 * themed confirm for cancel session / remove participant), fed with getScheduleDetail data. The pop-ups
 * are rendered inside the drawer's <Dialog> so headlessui treats them as nested dialogs.
 */

const HOUR_PX = 76;
/** Placeholder blocks per weekday while a week loads: [hours after the first visible hour, length in hours]. */
const SKELETON_SLOTS = [[[1, 1], [10, 1]], [[3.5, 1]], [[0.5, 1], [10.5, 0.75]], [[2, 1.5]], [[9.5, 1]], [[1.5, 1]], []];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MON_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// White text on all of these passes contrast.
const CLASS_COLORS = ["#C8243A", "#481B3A", "#6B00BD", "#155E75", "#9A3412", "#3F6212", "#9D174D", "#3730A3"];

const startOfWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = (x.getDay() + 6) % 7; // Monday = 0
  x.setDate(x.getDate() - diff);
  return x;
};
const addDaysLocal = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const pad = (n) => String(n).padStart(2, "0");
const hhmm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const colorFor = (classId) => {
  const str = String(classId || "");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return CLASS_COLORS[h % CLASS_COLORS.length];
};

const bookedCount = (s) => {
  const list = Array.isArray(s.participants) ? s.participants : [];
  return list.reduce((sum, p) => sum + (parseInt(p?.bookingQty, 10) || 1), 0);
};
const capacityOf = (s) => {
  const cap = parseInt(s.capacity, 10);
  if (cap > 0) return cap;
  const list = Array.isArray(s.eventPricingList) ? s.eventPricingList : [];
  return list.reduce((sum, p) => sum + (parseInt(p?.capacity, 10) || 0), 0);
};
const isCancelled = (s) => ["cancelled", "refunded"].indexOf(String(s.eventStatus || "").toLowerCase()) > -1;
const locationLabel = (s) => s?.location?.addressName || [s?.location?.houseNumber, s?.location?.city].filter(Boolean).join(", ") || "";

function Icon({ d, className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {d}
    </svg>
  );
}

/** Lay out one day's sessions into side-by-side lanes when they overlap. */
const layoutDay = (sessions) => {
  const items = sessions
    .map((s) => ({ s, start: new Date(s.classDate), end: new Date(s.classEndDate || s.classDate) }))
    .sort((a, b) => a.start - b.start);
  const out = [];
  let cluster = [];
  let clusterEnd = 0;
  const flush = () => {
    const lanes = [];
    cluster.forEach((it) => {
      let lane = lanes.findIndex((endT) => endT <= it.start.getTime());
      if (lane === -1) {
        lane = lanes.length;
        lanes.push(0);
      }
      lanes[lane] = it.end.getTime();
      it.lane = lane;
    });
    cluster.forEach((it) => out.push({ ...it, lanes: lanes.length }));
    cluster = [];
  };
  items.forEach((it) => {
    if (cluster.length && it.start.getTime() >= clusterEnd) flush();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.end.getTime());
  });
  if (cluster.length) flush();
  return out;
};

/** Class photo in the list view; falls back to the class colour with initials when there is no photo or it fails to load. */
function ClassThumb({ src, name, color, dim }) {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [src]);
  const url = typeof src === "string" ? src.trim() : "";
  const box = classNames("h-11 w-11 shrink-0 overflow-hidden rounded-xl sm:h-12 sm:w-12", dim ? "opacity-50 grayscale" : "");
  if (url && !failed) {
    return (
      <span className={box}>
        <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} className="h-full w-full object-cover" />
      </span>
    );
  }
  const initials =
    String(name || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join("") || "•";
  return (
    <span aria-hidden="true" className={classNames(box, "flex items-center justify-center text-sm font-black text-white")} style={{ background: color }}>
      {initials}
    </span>
  );
}

function BookedBar({ booked, capacity, light = true }) {
  const pct = capacity > 0 ? Math.min(100, Math.round((booked / capacity) * 100)) : 0;
  return (
    <span className={classNames("block h-1 w-full overflow-hidden rounded-full", light ? "bg-white/30" : "bg-[#EDE7F0]")}>
      <span className={classNames("block h-1 rounded-full", light ? "bg-white" : "bg-indigo-bz")} style={{ width: `${pct}%` }} />
    </span>
  );
}

export default function MySchedule({ title = "My Schedule" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { addErrorToast, addSuccessToast } = useToast();
  const userReduxState = useSelector(userState);
  const dashboardReduxState = useSelector(dashboardState);

  const [weekStart, setWeekStart] = React.useState(() => startOfWeek(new Date()));
  const [view, setView] = React.useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "list" : "week"));
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [classList, setClassList] = React.useState([]);
  const [classesLoaded, setClassesLoaded] = React.useState(false);
  const [classFilter, setClassFilter] = React.useState("");
  const [selected, setSelected] = React.useState(null);
  const [detail, setDetail] = React.useState(null); // getScheduleDetail of the selected session (what the dialogs need)
  const [action, setAction] = React.useState(""); // "time" | "location" | "participant" | "cancel"
  const [removing, setRemoving] = React.useState(null); // participant to remove (confirm dialog)
  const membersCacheRef = React.useRef(null); // club members list, to find a member's page id
  const [now, setNow] = React.useState(new Date());
  const [addSessions, setAddSessions] = React.useState({
    open: !!routeLocation.state?.addSessionsFor,
    classId: routeLocation.state?.addSessionsFor || "",
  });

  React.useEffect(() => {
    dispatch(setSectionLoading(false)); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const loadClasses = React.useCallback(async () => {
    try {
      const res = await axiosInstance({
        method: "get",
        url: `${getUrlForClasses()}listOfClasses?isPackage=false&listFor=${userReduxState.businessType}&onlyClassDetail=true`,
      });
      if (res.status === 200 && Array.isArray(res.data?.data)) {
        setClassList(res.data.data.filter((c) => !c.isDeleted).map((c) => ({ label: c.className, id: c._id, data: c })));
      }
    } catch (e) {
      // keep previous
    } finally {
      setClassesLoaded(true);
    }
  }, [userReduxState.businessType]);

  // Skeleton only when a week is shown for the first time; refreshes after an action stay silent.
  const loadedWeekRef = React.useRef("");
  const weekReqRef = React.useRef(0);
  const loadWeek = React.useCallback(async () => {
    if (!userReduxState.userData?._id) return;
    const key = weekStart.toISOString();
    const req = ++weekReqRef.current;
    if (loadedWeekRef.current !== key) setLoading(true);
    try {
      const res = await axiosInstance({
        method: "get",
        url: `${getUrlForClasses("")}getMyClubSchedule?searchDate=${weekStart.toISOString()}&businessId=${userReduxState.userData._id}&daysbeforeCurrentDate=0&daysAfterCurrentDate=7`,
      });
      if (req !== weekReqRef.current) return; // a newer week was requested meanwhile
      loadedWeekRef.current = key;
      if (res.status === 200 && res.data?.statusCode === 200) {
        const data = res.data.data || {};
        const flat = Array.isArray(data) ? data : Object.values(data).flat();
        const from = weekStart.getTime();
        const to = addDaysLocal(weekStart, 7).getTime();
        const seen = {};
        const weekSessions = flat.filter((s) => {
          if (!s || !s.classDate || seen[s._id]) return false;
          seen[s._id] = true;
          const t = new Date(s.classDate).getTime();
          return t >= from && t < to;
        });
        setSessions(weekSessions);
        // keep an open session drawer in sync (e.g. after removing a participant)
        setSelected((prev) => (prev ? weekSessions.find((x) => x._id === prev._id) || prev : prev));
      } else {
        setSessions([]);
      }
    } catch (e) {
      if (req === weekReqRef.current) setSessions([]);
    } finally {
      if (req === weekReqRef.current) setLoading(false);
    }
  }, [weekStart, userReduxState.userData?._id]);

  React.useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  React.useEffect(() => {
    loadWeek();
  }, [loadWeek, dashboardReduxState.updateSessionComp]);

  const loadDetail = async (s) => {
    try {
      const res = await axiosInstance({
        method: "get",
        url: `${getUrlForClasses()}getScheduleDetail?scheduleclassId=${s._id}&isPackage=${s?.isPackage || false}&onlySingleSchedule=true`,
      });
      if (res.status === 200 && res.data?.statusCode === 200) return res.data.data;
      addErrorToast(typeof res.data?.message === "string" ? res.data.message : "Couldn't load this session.");
    } catch (e) {
      addErrorToast("Couldn't load this session.");
    }
    return null;
  };

  const openCard = async (s) => {
    setSelected(s);
    setDetail(null);
    const d = await loadDetail(s);
    setDetail((prev) => (d && d.instanceId ? d : d ? { ...d, instanceId: s._id } : prev));
  };

  const startAction = (key) => {
    if (!detail) return;
    setAction(key); // pop-up opens on top of the session drawer, which stays open
  };

  // After a pop-up saved: patch the open drawer right away, reload the week and the session detail.
  const afterSessionChange = async (patch) => {
    if (patch) setSelected((prev) => (prev ? { ...prev, ...patch } : prev));
    reloadAfterAction();
    const cur = selected;
    if (cur) {
      const d = await loadDetail(cur);
      if (d) setDetail(d.instanceId ? d : { ...d, instanceId: cur._id });
    }
  };

  const viewMember = async (p) => {
    try {
      if (!membersCacheRef.current) {
        dispatch(setGlobalLoading(true));
        const res = await axiosInstance({
          method: "get",
          url: `${getUrlForClasses("")}getMyClubMembers?invite=used&getall=true&businessId=${userReduxState.userData?._id}`,
        });
        dispatch(setGlobalLoading(false));
        const raw = res?.data?.data;
        membersCacheRef.current = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      }
      const member = membersCacheRef.current.find((m) => String(m?.consumer_detail?.consumerId || m?.consumerId || "") === String(p.userId));
      if (member) {
        navigate(`/mymembers/detail?id=${p.userId}&mid=${member._id}`);
      } else {
        addErrorToast("This person isn't in your member list.");
      }
    } catch (e) {
      dispatch(setGlobalLoading(false));
      addErrorToast("Couldn't open this member.");
    }
  };

  const removeParticipant = async () => {
    const p = removing;
    if (!p || !selected) return;
    dispatch(setGlobalLoading(true));
    try {
      const request = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings()}cancelClassBooking`,
        data: {
          bookingId: p.bookingId,
          scheduleClassId: selected._id,
          type: "remove",
          isPackage: selected.isPackage || false,
          participantId: p.userId,
          familyMemberId: "",
        },
      });
      if (request.data?.statusCode === 200) {
        addSuccessToast(`${p.fullName || "Participant"} removed`);
        reloadAfterAction();
      } else {
        addErrorToast(typeof request.data?.message === "string" ? request.data.message : "Couldn't remove this participant.");
      }
    } catch (e) {
      addErrorToast("Couldn't remove this participant.");
    } finally {
      dispatch(setGlobalLoading(false));
      setRemoving(null);
    }
  };

  const reloadAfterAction = () => {
    dispatch(dashboardActions.setUpdateSessionComp(dashboardReduxState.updateSessionComp + 1));
  };

  const cancelSession = async () => {
    if (!detail) return;
    dispatch(setGlobalLoading(true));
    try {
      const result = await axiosInstance.delete(`${getUrlForClasses()}deleteScheduleTheClass`, {
        data: { scheduleId: detail.instanceId, isPackage: detail.isPackage || false },
      });
      if (result.data.statusCode === 200) {
        addSuccessToast("Session cancelled");
        setSelected(null);
        reloadAfterAction();
      } else {
        addErrorToast(typeof result.data.message === "string" ? result.data.message : "Couldn't cancel this session.");
      }
    } catch (e) {
      addErrorToast("Couldn't cancel this session.");
    } finally {
      dispatch(setGlobalLoading(false));
      setAction("");
    }
  };

  const colorIndex = {};
  classList.forEach((c, i) => {
    colorIndex[String(c.id)] = i;
  });
  // Latest class photo from the class itself, in case a session has none stored.
  const photoOf = (s) => s.classPhoto || classList.find((c) => String(c.id) === String(s.classId))?.data?.classPhoto || "";
  const colorOf = (classId) => (colorIndex[String(classId)] !== undefined ? CLASS_COLORS[colorIndex[String(classId)] % CLASS_COLORS.length] : colorFor(classId));

  const days = Array.from({ length: 7 }, (_, i) => addDaysLocal(weekStart, i));
  const visible = classFilter ? sessions.filter((s) => String(s.classId) === String(classFilter)) : sessions;
  // Location column whenever the sessions have a location.
  const multiLocation = visible.some((x) => !!locationLabel(x));
  const nowMs = Date.now();
  const byDay = days.map((d) => visible.filter((s) => sameDay(new Date(s.classDate), d)).sort((a, b) => new Date(a.classDate) - new Date(b.classDate)));

  // Hours shown: earliest to latest session of the week (at least 08:00–20:00).
  let minH = 8;
  let maxH = 20;
  visible.forEach((s) => {
    const st = new Date(s.classDate);
    const en = new Date(s.classEndDate || s.classDate);
    minH = Math.min(minH, st.getHours());
    maxH = Math.max(maxH, en.getHours() + (en.getMinutes() > 0 ? 1 : 0));
  });
  maxH = Math.min(24, Math.max(maxH, minH + 1));
  const hours = Array.from({ length: maxH - minH }, (_, i) => minH + i);
  const gridHeight = (maxH - minH) * HOUR_PX;

  const weekEnd = addDaysLocal(weekStart, 6);
  const weekLabel =
    weekStart.getMonth() === weekEnd.getMonth()
      ? `${weekStart.getDate()} – ${weekEnd.getDate()} ${MONTHS[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`
      : `${weekStart.getDate()} ${MON_SHORT[weekStart.getMonth()]} – ${weekEnd.getDate()} ${MON_SHORT[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`;
  const totalBooked = visible.filter((s) => !isCancelled(s)).reduce((sum, s) => sum + bookedCount(s), 0);
  const activeCount = visible.filter((s) => !isCancelled(s)).length;
  const today = new Date();
  const isThisWeek = sameDay(startOfWeek(today), weekStart);

  const sel = selected;
  const selBooked = sel ? bookedCount(sel) : 0;
  const selCap = sel ? capacityOf(sel) : 0;
  const selCancelled = sel ? isCancelled(sel) : false;

  return (
    <TwMainLayout
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          <div className="w-full">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-3xl">{title}</h1>
                <p className="mt-1 max-w-xl text-sm text-gray-600 sm:text-base">Your timetable. Members book these sessions with their credit passes and subscriptions.</p>
              </div>
              <button
                type="button"
                onClick={() => setAddSessions({ open: true, classId: "" })}
                className="flex items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-5 py-3 text-base font-extrabold text-white shadow-[0_10px_24px_rgba(72,27,58,0.25)] hover:bg-light-brown-coffee"
              >
                <Icon className="h-5 w-5" d={<path d="M12 5v14M5 12h14" />} />
                Add sessions
              </button>
            </div>

            {/* Toolbar */}
            <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" aria-label="Previous week" onClick={() => setWeekStart((w) => addDaysLocal(w, -7))} className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
                  <Icon d={<path d="m15 18-6-6 6-6" />} />
                </button>
                <button type="button" onClick={() => setWeekStart(startOfWeek(new Date()))} className={classNames("h-10 rounded-xl border px-4 text-sm font-extrabold", isThisWeek ? "border-[#E3D0F7] bg-[#F3E9FF] text-indigo-bz" : "border-gray-200 bg-white text-gray-900 hover:bg-gray-50")}>
                  Today
                </button>
                <button type="button" aria-label="Next week" onClick={() => setWeekStart((w) => addDaysLocal(w, 7))} className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
                  <Icon d={<path d="m9 18 6-6-6-6" />} />
                </button>
                <span className="ml-1 text-lg font-black text-gray-900">{weekLabel}</span>
                {loading ? (
                  <span className="h-4 w-36 animate-pulse rounded-full bg-[#EDE5F0]" aria-hidden="true" />
                ) : (
                  <span className="text-sm text-gray-500 sm:before:mr-1 sm:before:content-['·']">
                    {activeCount} {activeCount === 1 ? "session" : "sessions"} · {totalBooked} booked
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <select
                    aria-label="Filter by class"
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                    className="h-10 appearance-none rounded-xl border border-gray-200 bg-white pl-3 pr-9 text-sm font-bold text-gray-900 outline-none focus:border-indigo-bz"
                  >
                    <option value="">All classes</option>
                    {classList.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500"><Icon className="h-4 w-4" d={<path d="m6 9 6 6 6-6" />} /></span>
                </div>
                <div role="tablist" aria-label="View" className="flex gap-1 rounded-xl bg-[#F3EEF5] p-1">
                  {[["week", "Week"], ["list", "List"]].map(([k, l]) => (
                    <button
                      key={k}
                      role="tab"
                      aria-selected={view === k}
                      type="button"
                      onClick={() => setView(k)}
                      className={classNames("h-8 rounded-lg px-3 text-sm font-extrabold", view === k ? "bg-white text-brown-coffee shadow-sm" : "text-gray-500 hover:text-gray-900")}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* No classes at all */}
            {classesLoaded && classList.length === 0 ? (
              <div className="mt-8 flex flex-col items-center rounded-3xl border border-dashed border-[#E3D0F7] bg-white px-6 py-14 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
                  <Icon className="h-8 w-8" d={<><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18" /></>} />
                </span>
                <h2 className="mt-5 text-xl font-black text-gray-900">Create a class first</h2>
                <p className="mt-2 max-w-md text-sm text-gray-600 sm:text-base">Sessions are dates and times for one of your classes. Add a class, then put it on your timetable here.</p>
                <button type="button" onClick={() => navigate("/myclasses/new")} className="mt-6 rounded-2xl bg-brown-coffee px-6 py-3.5 text-base font-extrabold text-white hover:bg-light-brown-coffee">
                  Create a class
                </button>
              </div>
            ) : view === "week" ? (
              /* Week grid */
              <div className="mt-5 overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(72,27,58,0.06)]">
                <div className="min-w-[880px]">
                  <div className="grid grid-cols-[56px_repeat(7,minmax(0,1fr))] border-b border-gray-100">
                    <div />
                    {days.map((d) => {
                      const isToday = sameDay(d, today);
                      return (
                        <div key={dayKey(d)} className="flex flex-col items-center py-3">
                          <span className="text-xs font-extrabold uppercase tracking-wide text-gray-500">{DOW[d.getDay()]}</span>
                          <span className={classNames("mt-0.5 flex h-9 w-9 items-center justify-center rounded-full text-lg font-black", isToday ? "bg-portland-orange text-white" : "text-gray-900")}>{d.getDate()}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="max-h-[calc(100vh-300px)] min-h-[420px] overflow-y-auto">
                    <div className="relative grid grid-cols-[56px_repeat(7,minmax(0,1fr))]" style={{ height: gridHeight }} aria-busy={loading}>
                      {/* hour labels */}
                      <div className="relative">
                        {hours.map((h, i) => (
                          <span key={h} className="absolute right-2 -translate-y-1/2 text-xs font-bold text-gray-400" style={{ top: i * HOUR_PX }}>
                            {i === 0 ? "" : `${pad(h)}:00`}
                          </span>
                        ))}
                      </div>
                      {days.map((d, di) => {
                        const laid = layoutDay(byDay[di]);
                        const isToday = sameDay(d, today);
                        const nowTop = ((now.getHours() + now.getMinutes() / 60) - minH) * HOUR_PX;
                        return (
                          <div key={dayKey(d)} className={classNames("relative border-l border-gray-100", isToday ? "bg-[#FBF8FE]" : "")}>
                            {hours.map((h, i) => (
                              <div key={h} className="absolute inset-x-0 border-t border-gray-100" style={{ top: i * HOUR_PX }} />
                            ))}
                            {isToday && nowTop > 0 && nowTop < gridHeight && (
                              <div className="pointer-events-none absolute inset-x-0 z-20 flex items-center" style={{ top: nowTop }}>
                                <span className="-ml-1 h-2.5 w-2.5 rounded-full bg-portland-orange" />
                                <span className="h-0.5 flex-1 bg-portland-orange" />
                              </div>
                            )}
                            {loading &&
                              (SKELETON_SLOTS[di] || []).map(([at, len], k) => (
                                <div
                                  key={`sk${k}`}
                                  aria-hidden="true"
                                  className="absolute z-10 animate-pulse rounded-xl bg-[#EDE5F0] p-2"
                                  style={{ top: at * HOUR_PX + 1, height: len * HOUR_PX - 3, left: 3, right: 3 }}
                                >
                                  <span className="block h-2.5 w-10 rounded-full bg-white/70" />
                                  <span className="mt-2 block h-3 w-3/4 rounded-full bg-white/70" />
                                </div>
                              ))}
                            {!loading && laid.map(({ s, start, end, lane, lanes }) => {
                              const top = ((start.getHours() + start.getMinutes() / 60) - minH) * HOUR_PX;
                              const height = Math.max(28, ((end - start) / 3600000) * HOUR_PX - 3);
                              const cancelled = isCancelled(s);
                              const booked = bookedCount(s);
                              const cap = capacityOf(s);
                              const full = cap > 0 && booked >= cap;
                              const tall = height >= 58;
                              const showBar = height >= 72;
                              return (
                                <button
                                  key={s._id}
                                  type="button"
                                  onClick={() => openCard(s)}
                                  aria-label={`${s.className}, ${hhmm(start)} to ${hhmm(end)}, ${booked} of ${cap} booked${cancelled ? ", cancelled" : ""}`}
                                  className={classNames(
                                    "absolute z-10 overflow-hidden rounded-xl px-2 py-1.5 text-left transition hover:z-30 hover:shadow-lg focus:z-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-bz",
                                    cancelled ? "border border-dashed border-gray-300 bg-gray-50 text-gray-400" : "text-white"
                                  )}
                                  style={{
                                    top: top + 1,
                                    height,
                                    left: `calc(${(lane / lanes) * 100}% + 3px)`,
                                    width: `calc(${100 / lanes}% - 6px)`,
                                    background: cancelled ? undefined : colorOf(s.classId),
                                  }}
                                >
                                  <span className="flex items-center justify-between gap-1 text-[11px] font-extrabold opacity-90">
                                    <span>{hhmm(start)}</span>
                                    <span className={classNames("rounded-full px-1.5", cancelled ? "" : full ? "bg-white text-gray-900" : "bg-white/20")}>
                                      {cancelled ? "Cancelled" : full ? "Full" : `${booked}/${cap}`}
                                    </span>
                                  </span>
                                  <span className={classNames("block truncate text-[13px] font-extrabold leading-tight", cancelled ? "line-through" : "")}>{s.className}</span>
                                  {tall && s.instructorName && <span className="block truncate text-[11px] opacity-85">{s.instructorName}</span>}
                                  {showBar && !cancelled && (
                                    <span className="absolute inset-x-2 bottom-1.5">
                                      <BookedBar booked={booked} capacity={cap} />
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {!loading && visible.length === 0 && (
                    <div className="border-t border-gray-100 px-6 py-6 text-center text-sm text-gray-600">
                      No sessions this week.{" "}
                      <button type="button" onClick={() => setAddSessions({ open: true, classId: classFilter || "" })} className="font-extrabold text-indigo-bz hover:text-indigo-bz-hover">
                        Add sessions
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* List view */
              <div className="mt-5 flex flex-col gap-4">
                {days.map((d, di) => {
                  const list = byDay[di];
                  const isToday = sameDay(d, today);
                  return (
                    <section key={dayKey(d)} className="rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
                      <h2 className="flex items-center gap-2 text-sm font-extrabold text-gray-900">
                        {DOW[d.getDay()]} {d.getDate()} {MON_SHORT[d.getMonth()]}
                        {isToday && <span className="rounded-full bg-portland-orange px-2 py-0.5 text-[11px] font-extrabold text-white">Today</span>}
                      </h2>
                      {loading ? (
                        <div className="mt-3 flex flex-col gap-3" aria-hidden="true">
                          {Array.from({ length: (SKELETON_SLOTS[di] || []).length || 1 }).map((_, k) => (
                            <div key={k} className="flex animate-pulse items-center gap-3 sm:gap-4">
                              <span className="w-14 shrink-0 sm:w-16"><span className="block h-4 w-11 rounded-full bg-[#EDE5F0]" /><span className="mt-2 block h-3 w-9 rounded-full bg-[#F3EEF5]" /></span>
                              <span className="h-10 w-1.5 shrink-0 rounded-full bg-[#EDE5F0]" />
                              <span className="h-11 w-11 shrink-0 rounded-xl bg-[#EDE5F0] sm:h-12 sm:w-12" />
                              <span className="flex-1">
                                <span className="block h-4 w-1/2 rounded-full bg-[#EDE5F0]" />
                                <span className="mt-2 block h-3 w-1/3 rounded-full bg-[#F3EEF5]" />
                              </span>
                              <span className="hidden h-4 w-24 rounded-full bg-[#EDE5F0] sm:block" />
                            </div>
                          ))}
                        </div>
                      ) : list.length === 0 ? (
                        <p className="mt-2 text-sm text-gray-400">No sessions</p>
                      ) : (
                        <ul className="mt-2 divide-y divide-gray-100">
                          {list.map((s) => {
                            const start = new Date(s.classDate);
                            const end = new Date(s.classEndDate || s.classDate);
                            const cancelled = isCancelled(s);
                            const booked = bookedCount(s);
                            const cap = capacityOf(s);
                            const mins = Math.max(0, Math.round((end - start) / 60000));
                            const past = end.getTime() < nowMs;
                            const live = !cancelled && start.getTime() <= nowMs && end.getTime() > nowMs;
                            return (
                              <li key={s._id}>
                                <button
                                  type="button"
                                  onClick={() => openCard(s)}
                                  className={classNames("flex w-full items-center gap-3 rounded-2xl px-1 py-3 text-left hover:bg-[#FBF8FE] sm:gap-4 sm:px-2", past && !cancelled ? "opacity-60" : "")}
                                >
                                  {/* Time rail: start time + length */}
                                  <span className="w-14 shrink-0 sm:w-16">
                                    <span className={classNames("block text-base font-black tabular-nums tracking-tight sm:text-lg", cancelled ? "text-gray-400 line-through" : live ? "text-portland-orange" : "text-brown-coffee")}>{hhmm(start)}</span>
                                    {live ? (
                                      <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-[#FFE8EB] px-1.5 py-px text-[11px] font-extrabold text-portland-orange">
                                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-portland-orange" />
                                        Now
                                      </span>
                                    ) : (
                                      <span className="block text-xs font-bold text-gray-500">{mins} min</span>
                                    )}
                                  </span>
                                  <span className="h-10 w-1.5 shrink-0 rounded-full" style={{ background: cancelled ? "#D1D5DB" : colorOf(s.classId) }} />
                                  <ClassThumb src={photoOf(s)} name={s.className} color={cancelled ? "#D1D5DB" : colorOf(s.classId)} dim={cancelled} />
                                  <span className="min-w-0 flex-1">
                                    <span className={classNames("block truncate text-base font-extrabold", cancelled ? "text-gray-400 line-through" : "text-gray-900")}>{s.className}</span>
                                    {s.instructorName && <span className="hidden truncate text-xs text-gray-500 md:block">with {s.instructorName}</span>}
                                    <span className="block truncate text-xs text-gray-500 md:hidden">{[s.instructorName && `with ${s.instructorName}`, multiLocation && locationLabel(s)].filter(Boolean).join(" · ")}</span>
                                  </span>
                                  {multiLocation && (
                                    <span className="hidden w-44 min-w-0 shrink-0 items-center gap-1.5 text-sm text-gray-600 md:flex lg:w-56">
                                      <Icon className="h-4 w-4 shrink-0 text-gray-400" d={<><path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></>} />
                                      <span className="truncate">{locationLabel(s) || "–"}</span>
                                    </span>
                                  )}
                                  <span className="hidden w-32 shrink-0 sm:block">
                                    <span className="block text-right text-xs font-extrabold text-gray-700">{cancelled ? "Cancelled" : `${booked}/${cap} booked`}</span>
                                    {!cancelled && <span className="mt-1 block"><BookedBar booked={booked} capacity={cap} light={false} /></span>}
                                  </span>
                                  <span className="shrink-0 text-xs font-extrabold text-gray-700 sm:hidden">{cancelled ? "Cancelled" : `${booked}/${cap}`}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </div>

          {/* Session drawer: participants + actions menu */}
          <Dialog open={!!sel} onClose={() => setSelected(null)} className="relative z-50">
            <DialogBackdrop transition className="fixed inset-0 bg-[#1d1320]/45 transition-opacity duration-300 ease-out data-[closed]:opacity-0" />
            <div className="fixed inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10">
                <DialogPanel transition className="pointer-events-auto flex h-full w-screen max-w-xl transform flex-col bg-[#F7F4F8] shadow-2xl transition duration-300 ease-out data-[closed]:translate-x-full sm:rounded-l-3xl">
                  {sel && (
                    <>
                      {/* Header */}
                      <div className="border-b border-gray-200 bg-white px-5 py-5 sm:rounded-tl-3xl sm:px-7">
                        <div className="flex items-start gap-3">
                          {sel.classPhoto ? (
                            <img src={sel.classPhoto} alt="" className="h-14 w-14 shrink-0 rounded-2xl object-cover" />
                          ) : (
                            <span className="h-14 w-14 shrink-0 rounded-2xl" style={{ background: colorOf(sel.classId) }} />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-extrabold tracking-wide text-portland-orange">SESSION</p>
                            <DialogTitle className={classNames("truncate text-xl font-black tracking-tight text-gray-900", selCancelled ? "line-through" : "")}>{sel.className}</DialogTitle>
                          </div>
                          <button type="button" aria-label="Close" onClick={() => setSelected(null)} className="-mr-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100">
                            <Icon d={<path d="M18 6 6 18M6 6l12 12" />} />
                          </button>
                        </div>
                        <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
                          <div className="flex min-w-0 flex-col gap-1 text-sm text-gray-700">
                            <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-portland-orange" d={<><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18" /></>} />{DOW[new Date(sel.classDate).getDay()]} {new Date(sel.classDate).getDate()} {MON_SHORT[new Date(sel.classDate).getMonth()]} · {hhmm(new Date(sel.classDate))} – {hhmm(new Date(sel.classEndDate || sel.classDate))}</span>
                            {sel.instructorName && <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-portland-orange" d={<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>} />With {sel.instructorName}</span>}
                            {locationLabel(sel) && <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-portland-orange" d={<><path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></>} />{locationLabel(sel)}</span>}
                            {selCancelled && <span className="mt-1 inline-block self-start rounded-full bg-gray-100 px-2 py-0.5 text-xs font-extrabold text-gray-600">Cancelled</span>}
                          </div>
                          <Menu as="div" className="relative">
                            <MenuButton
                              aria-label="Session actions"
                              disabled={selCancelled}
                              className="flex h-11 items-center gap-1.5 rounded-2xl bg-brown-coffee px-4 text-sm font-extrabold text-white hover:bg-light-brown-coffee disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              Actions
                              <Icon className="h-4 w-4" d={<path d="m6 9 6 6 6-6" />} />
                            </MenuButton>
                            <MenuItems anchor="bottom end" className="z-[60] mt-1 w-56 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
                              {[
                                { key: "participant", label: "Add participant", icon: <><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0M19 8v6M16 11h6" /></> },
                                { key: "time", label: "Change date & time", icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></> },
                                { key: "location", label: "Change location", icon: <><path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></> },
                                { key: "coach", label: "Change coach", icon: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></> },
                              ].map((a) => (
                                <MenuItem key={a.key} disabled={!detail}>
                                  <button type="button" onClick={() => startAction(a.key)} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8] data-[disabled]:opacity-40">
                                    <Icon className="h-4 w-4 text-gray-500" d={a.icon} />
                                    {a.label}
                                  </button>
                                </MenuItem>
                              ))}
                              <div className="my-1 h-px bg-gray-100" />
                              <MenuItem disabled={!detail}>
                                <button type="button" onClick={() => startAction("cancel")} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-[#B4232F] data-[focus]:bg-[#FFF5F6] data-[disabled]:opacity-40">
                                  <Icon className="h-4 w-4" d={<><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6M9 9l6 6" /></>} />
                                  Cancel session
                                </button>
                              </MenuItem>
                            </MenuItems>
                          </Menu>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-7">
                        <section className="rounded-3xl border border-gray-200 bg-white p-4 sm:p-5">
                          <div className="flex items-baseline justify-between">
                            <span className="text-base font-black text-gray-900">{selBooked} of {selCap} spots booked</span>
                            <span className="text-xs font-bold text-gray-500">{selCap > 0 ? `${Math.max(0, selCap - selBooked)} left` : ""}</span>
                          </div>
                          <div className="mt-2"><BookedBar booked={selBooked} capacity={selCap} light={false} /></div>
                        </section>

                        <section className="mt-4 rounded-3xl border border-gray-200 bg-white p-4 sm:p-5">
                          <h3 className="text-base font-black text-gray-900">Participants ({Array.isArray(sel.participants) ? sel.participants.length : 0})</h3>
                          {Array.isArray(sel.participants) && sel.participants.length > 0 ? (
                            <ul className="mt-2 divide-y divide-gray-100">
                              {sel.participants.map((p, i) => (
                                <li key={`${p.userId}-${p.bookingId || i}`} className="flex items-center gap-3 py-3">
                                  {p.profilePic ? (
                                    <img src={p.profilePic} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" />
                                  ) : (
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F3E9FF] text-sm font-black text-indigo-bz">{(p.fullName || "?").slice(0, 1)}</span>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-extrabold text-gray-900">
                                      {p.fullName || "Member"}
                                      {parseInt(p.bookingQty, 10) > 1 && <span className="ml-1 text-xs font-bold text-gray-500">× {p.bookingQty}</span>}
                                    </p>
                                    {p.username && <p className="truncate text-xs text-gray-600">{p.username}</p>}
                                    {p.bookingId && <span className="mt-1 inline-block rounded-lg bg-[#F3EEF5] px-2 py-0.5 font-mono text-[11px] font-bold text-gray-700">#{p.bookingId}</span>}
                                  </div>
                                  <Menu as="div" className="relative shrink-0">
                                    <MenuButton aria-label={`Options for ${p.fullName || "participant"}`} className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 data-[open]:bg-gray-100">
                                      <Icon d={<><circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" /></>} />
                                    </MenuButton>
                                    <MenuItems anchor="bottom end" className="z-[60] mt-1 w-60 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
                                      <MenuItem>
                                        <button type="button" onClick={() => viewMember(p)} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                                          <Icon className="h-4 w-4 text-gray-500" d={<><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="9" cy="11" r="2.5" /><path d="M5.5 17a3.5 3.5 0 0 1 7 0" /><path d="M15 10h3M15 14h3" /></>} />
                                          View member
                                        </button>
                                      </MenuItem>
                                      <MenuItem disabled={selCancelled}>
                                        <button type="button" onClick={() => setRemoving(p)} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-[#B4232F] data-[focus]:bg-[#FFF5F6] data-[disabled]:opacity-40">
                                          <Icon className="h-4 w-4" d={<><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0M16 11h6" /></>} />
                                          Remove participant
                                        </button>
                                      </MenuItem>
                                    </MenuItems>
                                  </Menu>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="py-4 text-center text-sm text-gray-500">No bookings yet.</p>
                          )}
                        </section>
                      </div>
                    </>
                  )}
                </DialogPanel>
              </div>
            </div>

            <FcConfirmDialog
              open={!!removing}
              danger
              title="Remove participant?"
              message={`${removing?.fullName || "This participant"} will be removed from this session. Any money or credits they used for it are refunded in full.`}
              confirmLabel="Remove participant"
              onClose={() => setRemoving(null)}
              onConfirm={removeParticipant}
            />

            {/* Action pop-ups (nested in the session drawer) */}
            <SessionTimeDrawer open={!!detail && action === "time"} onClose={() => setAction("")} detail={detail} onSaved={(patch) => afterSessionChange(patch)} />
            <SessionLocationDrawer open={!!detail && action === "location"} onClose={() => setAction("")} detail={detail} onSaved={() => afterSessionChange()} />
            <SessionCoachDrawer open={!!detail && action === "coach"} onClose={() => setAction("")} detail={detail} onSaved={(patch) => afterSessionChange(patch)} />
            <AddParticipantDrawer
              open={!!detail && action === "participant"}
              onClose={() => setAction("")}
              detail={detail}
              bookedUserIds={(sessions.find((x) => x._id === detail?.instanceId)?.participants || []).map((p) => String(p.userId))}
              onAdded={() => afterSessionChange()}
            />
            <FcConfirmDialog
              open={!!detail && action === "cancel"}
              danger
              title="Cancel this session?"
              message="Everyone booked gets their money or credits back. This can't be undone."
              confirmLabel="Cancel session"
              cancelLabel="Keep session"
              onClose={() => setAction("")}
              onConfirm={cancelSession}
            />
          </Dialog>


          <AddSessionsDrawer
            open={addSessions.open}
            initialClassId={addSessions.classId}
            classList={classList}
            onClose={() => {
              setAddSessions({ open: false, classId: "" });
              if (routeLocation.state?.addSessionsFor) navigate(routeLocation.pathname, { replace: true, state: {} });
            }}
            onAdded={() => {
              dispatch(dashboardActions.setUpdateSessionComp(dashboardReduxState.updateSessionComp + 1));
            }}
          />
        </div>
      }
    />
  );
}
